// src/pages/Sensor/components/SensorFactors.tsx

import { AddTwoTone } from '@mui/icons-material'
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSortBy, useTable } from 'react-table'

import { ContractId, ContractPool } from '@/types/domain/contract'

import { DDMMYYYY, normalizedSensorType, userLocales } from '../../../api'
import { HStack, KTextField, usePreloader } from '../../../components/'
import { useContractPool, useFactorApi } from '../../../hooks'

const DAY_IN_MS = 86400000

const GermanDate = data =>
  data.value
    ? new Date(data.value).toLocaleDateString(userLocales, DDMMYYYY)
    : ''

const germanNotation = value =>
  value === null || value === undefined ? '' : String(value).replace(',', '.')

const sortByPeriodStart = data =>
  [...data].sort(
    (a, b) =>
      new Date(a.attributes.period_start).getTime() -
      new Date(b.attributes.period_start).getTime()
  )

type SensorFactor = {
  period_start: string
  period_end: string
  basic_charge_per_year: number | string | null
  price_per_unit: number | string | null
  pulse_value: number | string | null
  caloric_value: number | string | null
  conversion_factor: number | string | null
  meter_conversion_factor: number | string | null
  transformer_ratio: number | string | null
  co2_factor: number | string | null
  number_of_employees: number | string | null
  area_in_sqm: number | string | null
  sensor_id: number
  entity_id: number | null
}

type SensorFactorsProps = {
  sensor: {
    entity_id: number
    type_in_folder: string
    sensor_type: string
    sub_sensor_target: string
    unit: string
  }
  contractId: ContractId
}

export const SensorFactors = ({ sensor, contractId }: SensorFactorsProps) => {
  const cPool: ContractPool = useContractPool()
  const {
    getAll: getSensorFactor,
    save: saveFactor,
    update: updateFactor,
    deleteSensorFactor: deleteFactor,
  } = useFactorApi()

  const { entity_id, type_in_folder, sensor_type, sub_sensor_target, unit } =
    sensor
  const { isLoading } = usePreloader()

  const contractData = cPool.find(c => Number(c.id) === contractId)

  const date_begin = contractData?.attributes?.date_begin ?? '2018-01-01'
  const date_expire =
    contractData?.attributes?.date_expire ??
    new Date(Date.now()).toISOString().split('T')[0]

  const sanitize = useCallback(
    date =>
      date < date_begin ? date_begin : date > date_expire ? date_expire : date,
    [date_begin, date_expire]
  )

  const basicFactor = useMemo<SensorFactor>(
    () => ({
      period_start: sanitize(date_begin),
      period_end: sanitize(date_expire),
      basic_charge_per_year: null,
      price_per_unit: null,
      pulse_value: 1,
      caloric_value: 1,
      conversion_factor: 1,
      meter_conversion_factor: 1,
      transformer_ratio: 1,
      co2_factor: null,
      number_of_employees: null,
      area_in_sqm: null,
      sensor_id: entity_id,
      entity_id: null,
    }),
    [date_begin, date_expire, entity_id, sanitize]
  )

  const [factors, setFactors] = useState([])
  const [factor, setFactor] = useState<SensorFactor>(basicFactor)
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState<number | null>(null)
  const [isNewFactor, setIsNewFactor] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [warningText, setWarningText] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const isEnergySensor =
    normalizedSensorType(sensor_type, type_in_folder, sub_sensor_target) ===
    'energy'

  const isWaterSensor =
    normalizedSensorType(sensor_type, type_in_folder, sub_sensor_target) ===
    'water'

  const reloadFactors = useCallback(async () => {
    const updated = await getSensorFactor(entity_id)

    if (updated) setFactors(updated)
  }, [getSensorFactor, entity_id])

  useEffect(() => {
    let active = true

    const fetchFactors = async () => {
      const data = await getSensorFactor(entity_id)
      if (active) setFactors(data ?? [])
    }

    fetchFactors()

    return () => {
      active = false
    }
  }, [getSensorFactor, entity_id])

  useEffect(() => {
    if (isLoading) return

    if (factors.length === 0) {
      setFactor(basicFactor)
      return
    }

    const sorted = sortByPeriodStart(factors)
    const lastFactor = sorted.at(-1)

    if (!lastFactor) return

    setFactor({
      ...lastFactor.attributes,
      entity_id: lastFactor.id,
      sensor_id: entity_id,
    })
  }, [isLoading, factors, basicFactor, entity_id])

  useEffect(() => {
    if (isLoading || factors.length < 2) {
      setShowWarning(false)
      setWarningText('')
      return
    }

    const sorted = sortByPeriodStart(factors)

    const hasOverlap = sorted.some((item, i) => {
      if (i === 0) return false

      return (
        new Date(item.attributes.period_start).getTime() <=
        new Date(sorted[i - 1].attributes.period_end).getTime()
      )
    })

    const hasGap = sorted.some((item, i) => {
      if (i === sorted.length - 1) return false

      const currentEnd = new Date(item.attributes.period_end).getTime()
      const nextStart = new Date(
        sorted[i + 1].attributes.period_start
      ).getTime()

      return currentEnd + DAY_IN_MS !== nextStart
    })

    if (hasOverlap) {
      setShowWarning(true)
      setWarningText(
        'Es gibt Überschneidungen zwischen den gesetzten Zeiträumen'
      )
      return
    }

    if (hasGap) {
      setShowWarning(true)
      setWarningText('Es gibt Lücken zwischen den gesetzten Zeiträumen')
      return
    }

    setShowWarning(false)
    setWarningText('')
  }, [isLoading, factors])

  const data = useMemo(
    () =>
      factors.map(item => ({
        ...item.attributes,
        entity_id: item.id,
        sensor_id: entity_id,
      })),
    [factors, entity_id]
  )

  const columns = useMemo(() => {
    const allColumns = [
      {
        accessor: 'period_start',
        Cell: GermanDate,
        Header: (
          <Tooltip title={'Beginn des Zeitraums festlegen'}>
            <Typography>Von</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'period_end',
        Cell: GermanDate,
        Header: (
          <Tooltip title={'Ende des Zeitraums festlegen'}>
            <Typography>Bis</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'basic_charge_per_year',
        Header: (
          <Tooltip title={'Die gesamten Fixkosten des Zeitraums festlegen'}>
            <Typography>Fixkosten</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'price_per_unit',
        Header: (
          <Tooltip title={'Die variablen Kosten des Zeitraums festlegen'}>
            <Typography>Variable Kosten {String(unit)}</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'pulse_value',
        Header: (
          <Tooltip title={'Den Sensor Faktor des Zeitraums festlegen'}>
            <Typography>Impulsfaktor</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'caloric_value',
        Header: (
          <Tooltip title={'Den Brennwert Faktor des Zeitraums festlegen'}>
            <Typography>Brennwert</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'meter_conversion_factor',
        Header: (
          <Tooltip
            title={
              'Den Umrechnungsfaktor für den Wärmezähler des Zeitraums festlegen'
            }
          >
            <Typography>Umrechnungsfaktor</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'transformer_ratio',
        Header: (
          <Tooltip title={'Den Wandlerfaktor Faktor des Zeitraums festlegen'}>
            <Typography>Wandlerfaktor</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'conversion_factor',
        Header: (
          <Tooltip
            title={'Den Faktor für die Zustandszahl des Zeitraums festlegen'}
          >
            <Typography>Zustandszahl</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'co2_factor',
        Header: (
          <Tooltip title={'Den CO₂ Faktor für den Zeitraum festlegen'}>
            <Typography>CO₂ Faktor in kg pro kWh</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'number_of_employees',
        Header: (
          <Tooltip
            title={'Die Anzahl der Mitarbeiter für den Zeitraum festlegen'}
          >
            <Typography>Anzahl Mitarbeiter</Typography>
          </Tooltip>
        ),
      },
      {
        accessor: 'area_in_sqm',
        Header: (
          <Tooltip title={'Die Gebäudefläche für den Zeitraum festlegen'}>
            <Typography>Fläche in m²</Typography>
          </Tooltip>
        ),
      },
    ]

    if (isEnergySensor) {
      return allColumns.filter(
        item =>
          item.accessor !== 'caloric_value' &&
          item.accessor !== 'conversion_factor'
      )
    }

    if (isWaterSensor) {
      return allColumns.filter(
        item =>
          item.accessor !== 'co2_factor' &&
          item.accessor !== 'caloric_value' &&
          item.accessor !== 'conversion_factor'
      )
    }

    return allColumns
  }, [isEnergySensor, isWaterSensor, unit])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageSize: data.length },
      },
      useSortBy
    )

  const handleTableClick = rowInfo => {
    setOpen(true)
    setIsNewFactor(false)
    setFactor({ ...rowInfo.original })
    setIndex(rowInfo.index)
  }

  const onSaveFactor = async (sensorData: SensorFactor) => {
    if (isNewFactor) return saveFactor(sensorData)

    if (!sensorData.entity_id)
      throw new Error('Factor entity_id fehlt für Update')

    return updateFactor(sensorData, sensorData.entity_id)
  }

  const onDeleteFactor = async (sensorData: SensorFactor) => {
    if (!sensorData.entity_id)
      throw new Error('Factor entity_id fehlt für Delete')

    return deleteFactor(sensorData.entity_id, entity_id)
  }

  const onSave = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      await onSaveFactor(factor)
      setFactors(prev =>
        prev.map(item =>
          String(item.id) === String(factor.entity_id)
            ? {
                ...item,
                attributes: {
                  ...item.attributes,
                  ...factor,
                },
              }
            : item
        )
      )
      await reloadFactors()
      setOpen(false)
      setIndex(null)
    } finally {
      setIsSaving(false)
    }
  }

  const onDelete = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      await onDeleteFactor(factor)
      await reloadFactors()
      setOpen(false)
      setIndex(null)
    } finally {
      setIsSaving(false)
    }
  }

  const onAdd = () => {
    setFactor({ ...basicFactor })
    setIsNewFactor(true)
    setIndex(null)
    setOpen(true)
  }

  const onCancel = () => {
    setOpen(false)
    setIndex(null)
  }

  return (
    <>
      <Dialog maxWidth={'lg'} open={open} onClose={onCancel}>
        <DialogTitle>
          Faktoren für Zeitraum {isNewFactor ? 'erfassen' : 'bearbeiten'}
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            inputProps={{
              min: sanitize(date_begin),
              max: sanitize(date_expire),
            }}
            label={'Beginn Zeitraum'}
            onChange={e =>
              setFactor(prev => ({ ...prev, period_start: e.target.value }))
            }
            type={'date'}
            value={factor.period_start}
          />

          <TextField
            fullWidth
            inputProps={{
              min: sanitize(date_begin),
              max: sanitize(date_expire),
            }}
            label={'Ende Zeitraum'}
            onChange={e =>
              setFactor(prev => ({ ...prev, period_end: e.target.value }))
            }
            type={'date'}
            value={factor.period_end}
          />

          <KTextField
            error={!factor.pulse_value}
            inputProps={{ min: 0 }}
            label={'Impulswert Multiplikator'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                pulse_value: e.target.value,
              }))
            }
            type={'number'}
            value={germanNotation(factor.pulse_value)}
          />

          <KTextField
            label={'Mitarbeiter'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                number_of_employees: e.target.value,
              }))
            }
            type={'number'}
            value={germanNotation(factor.number_of_employees)}
          />

          <KTextField
            label={'Fläche'}
            onChange={e =>
              setFactor(prev => ({ ...prev, area_in_sqm: e.target.value }))
            }
            type={'number'}
            unit={'in m²'}
            value={germanNotation(factor.area_in_sqm)}
          />

          <KTextField
            disabled={isEnergySensor || isWaterSensor}
            label={'Brennwert'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                caloric_value: e.target.value,
              }))
            }
            value={germanNotation(factor.caloric_value)}
          />

          <KTextField
            disabled={isEnergySensor || isWaterSensor}
            label={'Umrechnungsfaktor'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                meter_conversion_factor: e.target.value,
              }))
            }
            value={germanNotation(factor.meter_conversion_factor)}
          />

          <KTextField
            disabled={isEnergySensor || isWaterSensor}
            label={'Zustandszahl'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                conversion_factor: e.target.value,
              }))
            }
            value={germanNotation(factor.conversion_factor)}
          />

          <KTextField
            label={'Wandlerfaktor'}
            onChange={e =>
              setFactor(prev => ({
                ...prev,
                transformer_ratio: e.target.value,
              }))
            }
            value={germanNotation(factor.transformer_ratio)}
          />

          <KTextField
            disabled={isWaterSensor}
            label={'CO₂ Faktor'}
            onChange={e =>
              setFactor(prev => ({ ...prev, co2_factor: e.target.value }))
            }
            unit={'kg pro kWh'}
            value={germanNotation(factor.co2_factor)}
          />

          <HStack>
            <KTextField
              label={`variable Kosten pro ${unit}`}
              onChange={e =>
                setFactor(prev => ({ ...prev, price_per_unit: e.target.value }))
              }
              unit={'€'}
              value={germanNotation(factor.price_per_unit)}
            />

            <KTextField
              label={'Fixkosten pro Jahr'}
              onChange={e =>
                setFactor(prev => ({
                  ...prev,
                  basic_charge_per_year: e.target.value,
                }))
              }
              unit={'€'}
              value={germanNotation(factor.basic_charge_per_year)}
            />
          </HStack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={isNewFactor || index === null || isSaving}
            onClick={onDelete}
          >
            Löschen
          </Button>
          <Button disabled={isSaving || !factor.pulse_value} onClick={onSave}>
            Speichern
          </Button>
          <Button disabled={isSaving} onClick={onCancel}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      <Collapse in={showWarning}>
        <Alert severity={'warning'}>{warningText}</Alert>
      </Collapse>

      <TableContainer>
        <Table sx={{ width: 1 }} {...getTableProps()}>
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map(column => (
                  <TableCell
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row)

              return (
                <TableRow
                  key={row.id}
                  onClick={() => handleTableClick(row)}
                  sx={{ cursor: 'pointer' }}
                  {...row.getRowProps()}
                >
                  {row.cells.map(cell => (
                    <TableCell
                      key={`${row.id}-${cell.column.id}`}
                      {...cell.getCellProps()}
                    >
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <IconButton onClick={onAdd}>
        <AddTwoTone />
      </IconButton>
    </>
  )
}
